import React, { useEffect, useState } from 'react';
import Icons from '../../../components/icons';
import {
  ActionButton,
  ActionsMenu,
  ActionWrapper,
  ActiveContainer,
  HelperText,
  ImagePreview,
  InputFieldWrapper,
  ProfileFormWrapper,
  ProfileImageBox,
  StaffFormInputWrapper,
  StaffFormWrapper,
  UploadButton
} from '../styled';
import {
  ActivityClubOptions,
  ConfirmationDialogMessages,
  StaffActionItems
} from '../config/constant';
import TextInputWithIcon from '../../../components/TextInputWithIcon';
import { TeacherProfile, UpdateStaffDetailProps } from '../types';
import users from '../../../apis/users';
import { SnackbarSeverityType, Status } from '../../../enums';
import ConfirmationDialog from '../../../components/ConfirmationDialog/ConfirmationDialog';
import { isEmpty } from 'lodash';
import pallet from '../../../layouts/pallet';
import userApi from '../../../apis/users';
import CustomMenuItem from '../../../components/CustomMenuItem/CustomMenuItem';
import SelectAll from '../../../components/SelectAllField/SelectAllField';

const StaffBasicForm = ({
  isUserTeacherStaff,
  staffDetailData,
  formData,
  setFormData,
  showToast,
  onStatusUpdate
}: {
  isUserTeacherStaff: boolean;
  staffDetailData: TeacherProfile | undefined;
  formData: UpdateStaffDetailProps | undefined;
  setFormData: React.Dispatch<
    React.SetStateAction<UpdateStaffDetailProps | undefined>
  >;
  showToast: (message: string, severity: SnackbarSeverityType) => void;
  onStatusUpdate: () => void;
}) => {
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const isActionsOpen = Boolean(actionsAnchorEl);
  const [staffProfile, setStaffProfile] = useState<string>(
    staffDetailData?.profile || ''
  );

  useEffect(() => {
    setStaffProfile(staffDetailData?.profile || '');
  }, [staffDetailData]);

  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [confirmationDialogMessage, setConfirmationDialogMessage] =
    useState('');
  const [actionToConfirm, setActionToConfirm] = useState('');

  const visibleActionItems = StaffActionItems.filter((item) => {
    const { key } = item;

    const conditionToShowStudentActionItem: Record<string, boolean> = {
      activate_student: staffDetailData?.status === Status.INACTIVE,
      deactivate_student: staffDetailData?.status === Status.ACTIVE,
      enable_for_current_ay:
        isEmpty(staffDetailData?.currentCourseInfo) &&
        staffDetailData?.status === Status.ACTIVE &&
        isUserTeacherStaff
    };

    return conditionToShowStudentActionItem[key] ?? true;
  });

  const handleOpenActions = (event: React.MouseEvent<HTMLButtonElement>) => {
    setActionsAnchorEl(event.currentTarget);
  };
  const handleCloseActions = () => setActionsAnchorEl(null);

  const updateUserStatus = async (status: string, statusType: string) => {
    if (!staffDetailData?.id) return;
    try {
      await users.updateUserStatus(staffDetailData.id, status);
      onStatusUpdate();
      showToast(
        `User ${statusType} successfully`,
        SnackbarSeverityType.success
      );
    } catch (error: any) {
      showToast(
        error.error?.[0].message || 'Error updating user status',
        SnackbarSeverityType.error
      );
    }
  };

  const handleOpenConfirmationDialog = (message: string, action: string) => {
    setConfirmationDialogMessage(message);
    setActionToConfirm(action);
    setConfirmationDialogOpen(true);
  };

  const handleCloseConfirmationDialog = () => {
    setConfirmationDialogOpen(false);
    setConfirmationDialogMessage('');
    setActionToConfirm('');
  };

  const handleEnableCurrentAY = async () => {
    if (!staffDetailData?.id) return;
    try {
      const payload = {
        event: 'ADD_ACADEMIC_YEAR_WISE_MAPPING',
        subjects: [],
        isClassTeacher: false,
        classesToBeDeleted: []
      };
      await userApi.updateCourseInfo(staffDetailData?.id, payload);
      onStatusUpdate();
      showToast('New class added successfully', SnackbarSeverityType.success);
    } catch (error: any) {
      showToast(
        error.errors?.[0].message || 'Error adding new class',
        SnackbarSeverityType.error
      );
    }
  };

  const handleConfirmAction = async () => {
    handleCloseConfirmationDialog();
    handleCloseActions();

    const studentActionFunctionMap: Record<string, () => Promise<void>> = {
      activate_student: () => updateUserStatus(Status.ACTIVE, 'activated'),
      deactivate_student: () =>
        updateUserStatus(Status.INACTIVE, 'deactivated'),
      enable_for_current_ay: () => handleEnableCurrentAY()
    };

    const studentActionFunction = studentActionFunctionMap[actionToConfirm];
    if (studentActionFunction) {
      await studentActionFunction();
    }
  };

  const handleActionClick = async (actionKeyName: string) => {
    const message =
      ConfirmationDialogMessages[actionKeyName] ||
      ConfirmationDialogMessages.default;
    handleOpenConfirmationDialog(message, actionKeyName);
  };

  const handleOnChange = (
    e: React.ChangeEvent<{
      name?: string;
      value?: unknown;
      id?: string;
    }>
  ) => {
    const { id, value } = e.target;
    if (!id) return;
    setFormData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [id]: value as string
      };
    });
  };

  const handleActivityClubChange = (selected: string[]) => {
    setFormData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        currentActivityClubs: selected
      };
    });
  };

  const handleStaffProfileUploadClick = () => {
    const input = document.getElementById(
      `Staff-profile-upload`
    ) as HTMLInputElement;
    input?.click();
  };

  const uploadProfilePhoto = async (file: File | null) => {
    if (!file) return;
    const MAX_SIZE = 800 * 1024;
    if (file.size > MAX_SIZE) {
      showToast(
        'File size should not exceed 800KB',
        SnackbarSeverityType.error
      );
      return;
    }

    try {
      const response = await users.uploadUserProfilePhoto(
        staffDetailData?.id ?? '',
        file,
        false
      );

      const newProfileImage = response as any;

      setStaffProfile(newProfileImage.uploadedPath);

      showToast(
        'User profile photo uploaded successfully',
        SnackbarSeverityType.success
      );
    } catch (error: any) {
      showToast(
        error.errors?.[0].message || 'Error uploading user profile photo',
        SnackbarSeverityType.error
      );
    }
  };

  const getUserStatus = (): string => {
    let status = staffDetailData?.status === 'ACTIVE' ? 'Active' : 'InActive';

    if (!isUserTeacherStaff) {
      const currentAcademicYear =
        staffDetailData?.currentCourseInfo?.academicYear;
      if (!isEmpty(currentAcademicYear)) {
        status =
          status +
          ' | ' +
          currentAcademicYear.from.split('T')[0] +
          ' - ' +
          currentAcademicYear.to.split('T')[0];
      }
    }
    return status;
  };

  return (
    <>
      <StaffFormWrapper>
        <ProfileImageBox>
          <ImagePreview src={staffProfile} alt='Staff Profile' />

          <input
            type='file'
            style={{ display: 'none' }}
            id={`Staff-profile-upload`}
            accept='.jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif'
            onChange={async (e) => {
              const file = e.target.files?.[0] || null;
              await uploadProfilePhoto(file);
            }}
          />

          <UploadButton
            fullWidth
            startIcon={<Icons.ImageFillPrimary />}
            onClick={handleStaffProfileUploadClick}
          >
            Upload Photo
          </UploadButton>
          <HelperText>Allowed JPG, GIF or PNG. Max size of 800K</HelperText>
        </ProfileImageBox>
        <ProfileFormWrapper>
          <ActionWrapper>
            <ActiveContainer
              backgroundColor={
                staffDetailData?.status === 'ACTIVE'
                  ? pallet.lightGreen
                  : pallet.lightRed
              }
              color={
                staffDetailData?.status === 'ACTIVE'
                  ? pallet.green2
                  : pallet.red2
              }
            >
              {getUserStatus()}
            </ActiveContainer>
            <>
              <ActionButton
                onClick={handleOpenActions}
                isActionsOpen={isActionsOpen}
              >
                Actions
              </ActionButton>
              <ActionsMenu
                id='actions-menu'
                open={isActionsOpen}
                onClose={handleCloseActions}
                anchorEl={actionsAnchorEl}
              >
                {visibleActionItems.map((item) => (
                  <CustomMenuItem
                    key={item.key}
                    onClick={() => handleActionClick(item.key)}
                  >
                    {item.label}
                  </CustomMenuItem>
                ))}
              </ActionsMenu>
            </>
          </ActionWrapper>
          <StaffFormInputWrapper>
            <InputFieldWrapper>
              <TextInputWithIcon
                label='Name'
                placeholder='Enter Name'
                name='name'
                value={formData?.name || ''}
                onChange={handleOnChange}
                fullWidth
                required
              />
            </InputFieldWrapper>
            <InputFieldWrapper>
              <TextInputWithIcon
                label='Email'
                placeholder='Enter Email'
                name='email'
                type='email'
                value={formData?.email || ''}
                onChange={handleOnChange}
                fullWidth
                required
              />
            </InputFieldWrapper>

            <InputFieldWrapper>
              <SelectAll
                label='Activity Clubs'
                onChange={handleActivityClubChange}
                options={ActivityClubOptions}
                selected={formData?.currentActivityClubs || []}
                hasTopLabel
                placeholder='Select Activity Clubs'
                formControlMargin='none'
              />
            </InputFieldWrapper>
          </StaffFormInputWrapper>
        </ProfileFormWrapper>
      </StaffFormWrapper>
      <ConfirmationDialog
        open={confirmationDialogOpen}
        onClose={handleCloseConfirmationDialog}
        onConfirm={handleConfirmAction}
        message={confirmationDialogMessage}
      />
    </>
  );
};

export default StaffBasicForm;
